import axios, { type AxiosError, type AxiosInstance } from "axios";
import { getToken } from "../services/storageService";
import { logoutOnSessionExpired } from "./logout";

function requestUrl(error: AxiosError): string {
  const base = error.config?.baseURL ?? "";
  const path = error.config?.url ?? "";
  try {
    return new URL(path, base || window.location.origin).href;
  } catch {
    return `${base}${path}`;
  }
}

function isAuthMeRequest(url: string): boolean {
  return /\/client\/auth\/me(?:\?|$)/.test(url);
}

/**
 * Redirect only when a stored bearer is present and a non-/me API returns 401.
 * `/client/auth/me` is handled by `useAuthMe` (bearer → cookie retry).
 */
function handleAxiosUnauthorized(error: AxiosError) {
  if (error.response?.status !== 401) {
    return Promise.reject(error);
  }

  const url = requestUrl(error);
  if (isAuthMeRequest(url)) {
    return Promise.reject(error);
  }

  // Cookie-only / pre-auth probes must not bounce the user.
  if (!getToken()) {
    return Promise.reject(error);
  }

  void logoutOnSessionExpired();
  return Promise.reject(error);
}

function attachUnauthorizedInterceptor(instance: AxiosInstance): void {
  instance.interceptors.response.use((res) => res, handleAxiosUnauthorized);
}

/**
 * Must be imported first from `index.tsx` so every `axios.create()` and
 * `fetch()` used by the app / shared-dashboards can redirect on 401.
 */
export function installGlobalUnauthorizedRedirect(): void {
  if (typeof window === "undefined") return;
  const w = window as Window & { __variphi401RedirectInstalled?: boolean };
  if (w.__variphi401RedirectInstalled) return;
  w.__variphi401RedirectInstalled = true;

  attachUnauthorizedInterceptor(axios);

  const originalCreate = axios.create.bind(axios);
  axios.create = ((...args: Parameters<typeof axios.create>) => {
    const instance = originalCreate(...args);
    attachUnauthorizedInterceptor(instance);
    return instance;
  }) as typeof axios.create;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input, init);
    if (response.status !== 401) return response;

    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input.url;

    if (isAuthMeRequest(url)) return response;
    if (!getToken()) return response;

    void logoutOnSessionExpired();
    return response;
  };
}

installGlobalUnauthorizedRedirect();
